// src/AdvisorContext.js
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";

/**
 * AdvisorContext
 *
 * NOTE:
 * This is now the state + transport layer for OASYS Copilot.
 * The internal file/context names remain Advisor* temporarily to avoid
 * unnecessary churn while the visible product becomes OASYS Copilot.
 *
 * Responsibilities:
 * - Own Copilot messages and streaming state.
 * - Send Copilot messages to the backend ask endpoint.
 * - Include tenant/user/page context in requests.
 * - Provide quick-chip actions and fallbacks.
 * - Keep navigation link handling centralized.
 */

export const DEFAULT_INPUT_PLACEHOLDER =
  "Ask about risks, evidence, or next steps…";

const COPILOT_PERSONA = {
  name: "OASYS Copilot",
  initials: "OC",
  avatarColor: "#7dd3fc",
};

const COPILOT_MIN_THINK_MS = 1450;
const COPILOT_THINK_JITTER_MS = 650;

const COPILOT_GOVERNED_ASK_ONLY = true;

const COPILOT_CONVERSATION_VERSION = "oasys_conversation.v1";
const COPILOT_CONVERSATION_MAX_BROWSER_TURNS = 16;
const COPILOT_CONVERSATION_MAX_BROWSER_TURN_CHARS = 2200;
const COPILOT_CONVERSATION_MAX_BROWSER_TOTAL_CHARS = 9000;

const COPILOT_SELECTION_CONTEXT_KEYS = new Set([
  "record_type",
  "record_id",
  "task_id",
  "remediation_task_id",
  "work_item_id",
  "finding_id",
  "framework",
  "control_id",
  "rule_id",
  "resource_id",
  "resource_type",
  "workflow_id",
  "workflow_instance_id",
  "current_step_id",
  "validation_run_id",
  "validation_result_id",
  "validation_id",
  "result_id",
  "evidence_id",
  "sop_id",
  "alert_id",
  "drift_event_id",
  "drift_id",
  "connector_id",
  "connector_type",
  "provider",
  "source",
  "stream",
  "sync_job_id",
  "ai_interaction_id",
  "audit_event_id",
  "archive_id",
  "report_id",
  "audit_export_id",
  "submission_id",
  "priority_action_key",
  "priority_action_type",
  "priority_action_source_type",
  "leadership_action_key",
  "leadership_action_index",
  "leadership_action_title",
]);

const COPILOT_PRIMARY_SELECTION_KEYS = new Set([
  "record_type",
  "record_id",
  "task_id",
  "remediation_task_id",
  "finding_id",
  "control_id",
  "resource_id",
  "workflow_id",
  "workflow_instance_id",
  "validation_run_id",
  "validation_result_id",
  "validation_id",
  "result_id",
  "evidence_id",
  "sop_id",
  "alert_id",
  "drift_event_id",
  "drift_id",
  "connector_id",
  "sync_job_id",
  "ai_interaction_id",
  "audit_event_id",
  "archive_id",
  "report_id",
  "audit_export_id",
  "submission_id",
  "priority_action_key",
  "leadership_action_key",
]);

const COPILOT_UI_CONTEXT_KEYS = new Set([
  "page",
  "surface",
  "mode",
  "intelligence_mode",
  "window",
  "section",
  "tab",
  "filter",
  "queue",
  "record_scope",
  "owner_user_id",
  "context_mode",
  "remediationQueue",
  "confidence_scope",
]);

const COPILOT_SCOPE_ALIASES = {
  dashboard: "mission_control",
  "mission-control": "mission_control",
  mission_control: "mission_control",

  execution: "mission_execution",
  "mission-execution": "mission_execution",
  mission_execution: "mission_execution",
  "mission-execution-center": "mission_execution",
  mission_execution_center: "mission_execution",
  "execution-center": "mission_execution",
  execution_center: "mission_execution",
  "work-queue": "mission_execution",
  work_queue: "mission_execution",

  remediation: "remediation",
  "fix-plan": "remediation",
  fix_plan: "remediation",

  workflow: "workflow",
  workflows: "workflow",
  "workflow-center": "workflow",
  workflow_center: "workflow",

  validation: "validation",
  "validation-dashboard": "validation",
  validation_dashboard: "validation",
  "validation-intelligence": "validation",
  validation_intelligence: "validation",

  posture: "posture",
  compliance: "posture",

  alerts: "alerts",
  drift: "drift",
  "drift-state": "drift",
  drift_state: "drift",

  evidence: "evidence",
  "evidence-audit": "evidence",
  evidence_audit: "evidence",
  audit: "evidence",

  sop: "sop",
  "sop-center": "sop",
  sop_center: "sop",
  sopcenter: "sop",

  library: "sop",
  "document-library": "sop",
  document_library: "sop",

  admin: "admin",
  administration: "admin",
  "enterprise-admin": "admin",
  enterprise_admin: "admin",
  "enterprise-administration": "admin",
  enterprise_administration: "admin",
  "admin-center": "admin",
  admin_center: "admin",

  executive: "executive",
  "executive-intelligence": "executive",
  executive_intelligence: "executive",

  connectors: "connectors",
};

const COPILOT_CANONICAL_SCOPES = new Set([
  "mission_control",
  "mission_execution",
  "remediation",
  "workflow",
  "validation",
  "posture",
  "alerts",
  "drift",
  "evidence",
  "sop",
  "admin",
  "executive",
  "connectors",
]);

function normalizeCopilotScope(value, fallback = "mission_control") {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^#\/?/, "");

  if (!normalized) return fallback;

  return COPILOT_SCOPE_ALIASES[normalized] || normalized;
}

function resolveCopilotRequestScope(context = {}, fallback = "mission_control") {
  const candidates = [
    context.mode,
    context.page,
    context.surface,
    fallback,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeCopilotScope(candidate, "");
    if (COPILOT_CANONICAL_SCOPES.has(normalized)) {
      return normalized;
    }
  }

  return "mission_control";
}

function isSafeCopilotContextValue(value) {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

function hasCopilotSelectionUpdate(context = {}) {
  if (!context || typeof context !== "object") return false;

  return Object.keys(context).some((key) =>
    COPILOT_PRIMARY_SELECTION_KEYS.has(key)
  );
}

function preserveCopilotUiContext(context = {}) {
  const output = {};

  for (const [key, value] of Object.entries(context || {})) {
    if (!COPILOT_UI_CONTEXT_KEYS.has(key)) continue;
    if (value === undefined || value === null || value === "") continue;
    output[key] = value;
  }

  return output;
}

function sanitizeCopilotRequestContext(context = {}) {
  const source = context && typeof context === "object" ? context : {};
  const output = {};

  for (const [key, value] of Object.entries(source)) {
    if (
      !COPILOT_SELECTION_CONTEXT_KEYS.has(key) &&
      !COPILOT_UI_CONTEXT_KEYS.has(key)
    ) {
      continue;
    }

    if (!isSafeCopilotContextValue(value)) {
      continue;
    }

    if (typeof value === "string") {
      const cleaned = value.trim();
      if (!cleaned) continue;
      output[key] = cleaned;
      continue;
    }

    output[key] = value;
  }

  const canonicalScope = resolveCopilotRequestScope(
    output,
    "mission_control"
  );

  output.mode = canonicalScope;

  if (!output.page) {
    output.page = canonicalScope;
  }

  if (!output.surface) {
    output.surface = output.page || canonicalScope;
  }

  if (!output.intelligence_mode) {
    output.intelligence_mode = "page";
  }

  return compactObject(output);
}

function getRouteContextFingerprint() {
  if (typeof window === "undefined") return "server:dashboard";
  return String(window.location.hash || "#dashboard");
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getCopilotThinkDelay() {
  return COPILOT_MIN_THINK_MS + Math.floor(Math.random() * COPILOT_THINK_JITTER_MS);
}

export function getFollowupSuggestions(mode = "dashboard") {
  switch ((mode || "").toLowerCase()) {
    case "compliance":
    case "posture":
      return [
        "Explain the top failing controls",
        "Draft remediation next steps",
        "Show evidence to collect",
      ];

    case "alerts":
      return [
        "Triage recent alerts",
        "Explain control impact",
        "Recommend response steps",
      ];

    case "drift":
      return [
        "Explain recent drift",
        "Identify affected controls",
        "Suggest validation steps",
      ];

    case "evidence":
      return [
        "Find missing evidence",
        "Check audit readiness",
        "Summarize proof gaps",
      ];

    case "connectors":
      return [
        "Check failing connectors",
        "Show sync issues",
        "Recommend integration fixes",
      ];

    case "sop":
      return [
        "Which SOPs need attention?",
        "Find SOP-control gaps",
        "Suggest SOP updates",
      ];

    case "admin":
      return [
        "What administrative issues need attention?",
        "Which connectors need governance review?",
        "Summarize tenant readiness",
      ];

    case "billing":
    case "subscribe":
      return ["Usage this month", "Upcoming limits", "Top cost drivers"];

    default:
      return [
        "Review current risks",
        "Identify next actions",
        "Summarize workload",
      ];
  }
}



function getContextSuggestions(context = {}, mode = "dashboard") {
  const surface = String(context.surface || context.page || mode || "").toLowerCase();
  const taskStatus = String(context.task_status || "").toLowerCase();
  const ruleId = String(context.rule_id || "").trim();
  const hasFixPlan = surface.includes("fix_plan") || surface.includes("fix-plan");
  const hasExactTaskSelection = Boolean(
    context.task_id ||
      context.remediation_task_id ||
      context.work_item_id ||
      (String(context.record_type || "").toLowerCase() === "remediation_task" && context.record_id)
  );

  // Exact task selection gets stable operator questions that ask the backend to
  // resolve current task state authoritatively. Do not branch these prompts on
  // browser-authored task status, validation status, or closure state.
  if (hasExactTaskSelection) {
    return [
      "Help me fix this",
      "What evidence do I need?",
      "Why is closure blocked?",
    ];
  }

  if (hasFixPlan || context.control_id || ruleId) {
    if (taskStatus === "in_progress") {
      return [
        "What should I do next before validation?",
        "What evidence should I collect?",
        "How do I know this is ready for validation?",
      ];
    }

    if (taskStatus === "ready_for_validation") {
      return [
        "Can this task be validated now?",
        "What will Full Cloud Assessment check?",
        "What should I do if validation fails?",
      ];
    }

    if (context.copilot_draft_status === "applied") {
      return [
        "Summarize the applied remediation plan",
        "What evidence supports closure?",
        "What is the safest next step?",
      ];
    }

    return [
      ruleId ? `Explain ${ruleId}` : "Explain this control failure",
      "Draft remediation next steps",
      "What evidence should I collect?",
    ];
  }

  return getFollowupSuggestions(mode).slice(0, 3);
}


function getCopilotFocus(context = {}) {
  const label = String(context.focus_label || "").trim();
  if (!label) return null;

  const candidates = [
    ["task", context.task_id || context.remediation_task_id || context.work_item_id],
    ["finding", context.finding_id],
    ["workflow", context.workflow_instance_id || context.workflow_id],
    ["validation", context.validation_result_id || context.validation_run_id || context.validation_id],
    ["evidence", context.evidence_id],
    ["sop", context.sop_id],
    ["alert", context.alert_id],
    ["drift", context.drift_event_id || context.drift_id],
    ["connector", context.connector_id],
    ["record", context.record_id],
  ];

  const match = candidates.find(([, value]) => String(value || "").trim());
  const selectionKey = match
    ? `${match[0]}:${String(match[1]).trim()}`
    : `focus:${label}:${String(context.focus_type || "").trim()}`;

  return {
    label,
    type: String(context.focus_type || "").trim(),
    selectionKey,
  };
}


function sameCopilotFocus(left, right) {
  if (!left || !right) return false;

  const leftKey = String(left.selectionKey || "").trim();
  const rightKey = String(right.selectionKey || "").trim();

  if (leftKey && rightKey) {
    return leftKey === rightKey;
  }

  return (
    String(left.label || "").trim() === String(right.label || "").trim() &&
    String(left.type || "").trim() === String(right.type || "").trim()
  );
}


function createCopilotCorrelationId(prefix = "message") {
  const safePrefix = String(prefix || "message")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "message";

  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return `${safePrefix}:${crypto.randomUUID()}`;
    }
  } catch {}

  return `${safePrefix}:${Date.now().toString(36)}:${Math.random()
    .toString(36)
    .slice(2, 14)}`;
}


function getCopilotConversationBinding(context = {}, scope = "mission_control") {
  const normalizedScope = normalizeCopilotScope(scope, "mission_control");
  const recordType = String(context.record_type || "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");

  const recordTypeAliases = {
    remediation_task: "task",
    work_item: "task",
    task: "task",
    finding: "finding",
    control: "control",
    resource: "resource",
    workflow_instance: "workflow",
    workflow: "workflow",
    validation: "validation_run",
    validation_run: "validation_run",
    validation_result: "validation_result",
    evidence: "evidence",
    sop: "sop",
    alert: "alert",
    drift: "drift_event",
    drift_event: "drift_event",
    connector: "connector",
  };

  const candidates = [
    ["task", context.task_id || context.remediation_task_id || context.work_item_id],
    ["finding", context.finding_id],
    ["control", context.control_id],
    ["resource", context.resource_id],
    ["workflow", context.workflow_instance_id || context.workflow_id],
    ["validation_result", context.validation_result_id || context.result_id],
    ["validation_run", context.validation_run_id || context.validation_id],
    ["evidence", context.evidence_id],
    ["sop", context.sop_id],
    ["alert", context.alert_id],
    ["drift_event", context.drift_event_id || context.drift_id],
    ["connector", context.connector_id],
  ];

  let match = candidates.find(([, value]) => String(value || "").trim());

  if (!match && context.record_id && recordTypeAliases[recordType]) {
    match = [recordTypeAliases[recordType], context.record_id];
  }

  return {
    scope: normalizedScope,
    anchor_type: match ? match[0] : null,
    anchor_id: match ? String(match[1]).trim() : null,
  };
}


function sameCopilotConversationBinding(left, right) {
  const a = left && typeof left === "object" ? left : {};
  const b = right && typeof right === "object" ? right : {};

  return (
    String(a.scope || "") === String(b.scope || "") &&
    String(a.anchor_type || "") === String(b.anchor_type || "") &&
    String(a.anchor_id || "") === String(b.anchor_id || "")
  );
}


function projectAssistantMessageForConversation(message = {}) {
  const card =
    message?.card && typeof message.card === "object" ? message.card : null;

  // Only governed response cards are eligible assistant dialogue. Raw rendered
  // response text may contain record IDs and card metadata that must never be
  // promoted into conversational memory.
  if (!card) return "";

  const sections = [];

  const summary = String(card.summary || "").trim();
  if (summary) sections.push(`Summary: ${summary}`);

  const explanation = String(card.reasoning || card.explanation || "").trim();
  if (explanation) sections.push(`Why this guidance: ${explanation}`);

  const actions = Array.isArray(card.recommended_actions)
    ? card.recommended_actions
        .filter((item) => typeof item === "string" && item.trim())
        .slice(0, 8)
        .map((item, index) => `${index + 1}. ${item.trim()}`)
    : [];

  if (actions.length) {
    sections.push(`Recommended actions:\n${actions.join("\n")}`);
  }

  const limitations = Array.isArray(card.known_limitations)
    ? card.known_limitations
        .filter((item) => typeof item === "string" && item.trim())
        .slice(0, 5)
        .map((item) => `- ${item.trim()}`)
    : [];

  if (limitations.length) {
    sections.push(`Known limitations:\n${limitations.join("\n")}`);
  }

  return sections.join("\n\n").trim();
}


function buildCopilotConversationEnvelope({
  messages = [],
  requestContext = {},
  requestScope = "mission_control",
  conversationId,
  focusEpochId,
  currentMessageId,
  replyToMessageId = null,
}) {
  const currentBinding = getCopilotConversationBinding(
    requestContext,
    requestScope
  );

  const eligible = [];

  for (const message of Array.isArray(messages) ? messages : []) {
    if (!message || message.streaming) continue;
    if (!["user", "assistant"].includes(message.role)) continue;
    if (String(message.focusEpochId || "") !== String(focusEpochId || "")) continue;
    if (!sameCopilotConversationBinding(message.conversationBinding, currentBinding)) {
      continue;
    }

    const rawContent =
      message.role === "assistant"
        ? projectAssistantMessageForConversation(message)
        : String(message.content || "").trim();

    if (!rawContent) continue;

    eligible.push({
      message_id: String(message.messageId || "").trim() || null,
      role: message.role,
      content: rawContent.slice(0, COPILOT_CONVERSATION_MAX_BROWSER_TURN_CHARS),
      focus_epoch_id: focusEpochId,
      binding: currentBinding,
      interaction_id:
        message.role === "assistant"
          ? String(message.interactionId || "").trim() || null
          : null,
    });
  }

  let turns = eligible.slice(-COPILOT_CONVERSATION_MAX_BROWSER_TURNS);

  while (
    turns.length &&
    turns.reduce((total, turn) => total + String(turn.content || "").length, 0) >
      COPILOT_CONVERSATION_MAX_BROWSER_TOTAL_CHARS
  ) {
    turns = turns.slice(1);
  }

  return {
    version: COPILOT_CONVERSATION_VERSION,
    conversation_id: conversationId,
    focus_epoch_id: focusEpochId,
    current_message_id: currentMessageId,
    reply_to_message_id: replyToMessageId,
    turns,
    // Reserved for a future governed cross-focus comparison flow. v1 sends no
    // cross-focus references; the server keeps this capability disabled.
    reference_hints: [],
  };
}


const AdvisorContext = createContext({
  advisor: COPILOT_PERSONA,
  rotate: () => {},
  identity: "guest",
  advisorKey: "general",
  messages: [],
  isStreaming: false,
  error: null,
  sendMessage: async () => {},
  sendChip: async () => {},
  pushAssistantMessage: () => {},
  stopStreaming: () => {},
  onNavigate: undefined,
  copilotContext: {},
  governedCopilotContext: {},
  copilotFocus: null,
  contextSuggestions: [],
  setCopilotContext: () => {},
  clearCopilotContext: () => {},
  availableCopilotWorkflowActions: [],
  registerCopilotWorkflowAction: () => () => {},
  unregisterCopilotWorkflowAction: () => {},
  runCopilotWorkflowAction: async () => {},
  runGovernedCopilotAction: async () => {},
});

function normalizeBase(url) {
  return (url || "").replace(/\/+$/, "");
}

const ENV_ASK_PATH =
  (process.env.REACT_APP_ADVISOR_ASK_PATH || "/v1/ai/copilot/ask").trim();

const DASHBOARD_BASE =
  (process.env.REACT_APP_DASHBOARD_BASE || "/dashboard").trim();

const HASH_COMPLIANCE_BASE = (
  process.env.REACT_APP_HASH_COMPLIANCE_BASE || "compliance"
)
  .replace(/^\/+/, "")
  .trim();

const HASH_WINDOW_DEFAULT_DAYS = Number(
  process.env.REACT_APP_DASH_WINDOW_DEFAULT_DAYS || 90
);

function joinAskUrl(effectiveApiBase, envAskPathFallback) {
  const base = effectiveApiBase.replace(/\/+$/, "");
  let askPath = (envAskPathFallback || "/v1/ai/copilot/ask").replace(/^\/+/, "");

  if (askPath.startsWith("v1/")) {
    askPath = askPath.replace(/^v1\//, "");
  }

  return `${base}/${askPath}`;
}

function joinGovernedActionUrl(effectiveApiBase, endpoint) {
  const base = normalizeBase(effectiveApiBase);
  let path = String(endpoint || "").trim();

  if (!path || /^https?:\/\//i.test(path)) {
    throw new Error("Copilot governed actions require a relative OASYS API endpoint.");
  }

  path = path.replace(/^\/+/, "");

  if (base.toLowerCase().endsWith("/v1") && path.toLowerCase().startsWith("v1/")) {
    path = path.slice(3);
  }

  return `${base}/${path}`;
}

function getCurrentHashMode() {
  if (typeof window === "undefined") return "mission_control";

  const hash = String(window.location.hash || "")
    .replace(/^#\/?/, "")
    .split("?")[0]
    .trim()
    .toLowerCase();

  return normalizeCopilotScope(hash || "dashboard");
}

function getCurrentRouteParams() {
  if (typeof window === "undefined") return {};

  const hash = String(window.location.hash || "");
  const query = hash.includes("?") ? hash.split("?").slice(1).join("?") : "";

  if (!query) return {};

  try {
    return Object.fromEntries(new URLSearchParams(query).entries());
  } catch {
    return {};
  }
}

function compactObject(obj = {}) {
  return Object.fromEntries(
    Object.entries(obj || {}).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    })
  );
}

export function AdvisorProvider({
  children,
  apiBaseUrl,
  getAccessToken,
  onNavigate,
  userProfile = null,
  tenantId = "",
  userId = "",
  copilotContext = {},
}) {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();

  // Resolve API base from the explicit App prop first, then the App.js runtime authority.
  const effectiveApiBase = useMemo(() => {
    const explicitBase = normalizeBase(apiBaseUrl);
    const runtimeBase =
      typeof window !== "undefined" ? normalizeBase(window.__V1_BASE || "") : "";
    const base = explicitBase || runtimeBase;

    if (!base) {
      throw new Error(
        "OASYS API base is unavailable. App runtime configuration must initialize the API authority."
      );
    }

    return base;
  }, [apiBaseUrl]);

  const askUrl = useMemo(
    () => joinAskUrl(effectiveApiBase, ENV_ASK_PATH),
    [effectiveApiBase]
  );

  if (typeof window !== "undefined") {
    window.__ASK_URL__ = askUrl;
  }

  const identity = user?.email || user?.sub || "guest";

  // Stable Copilot identity.
  const advisorPersona = COPILOT_PERSONA;

  const resolvedTenantId =
    tenantId ||
    userProfile?.tenant_id ||
    userProfile?.tenantId ||
    userProfile?.app_metadata?.tenant_id ||
    userProfile?.app_metadata?.tenantId ||
    process.env.REACT_APP_TENANT_ID ||
    "";

  const resolvedUserId =
    userId ||
    userProfile?.user_id ||
    userProfile?.userId ||
    userProfile?.id ||
    userProfile?.app_metadata?.user_id ||
    userProfile?.app_metadata?.userId ||
    user?.["https://nutrx/user_id"] ||
    user?.["https://oasys/user_id"] ||
    user?.user_id ||
    process.env.REACT_APP_USER_ID ||
    "";

  const resolvedUserName =
    userProfile?.name ||
    user?.name ||
    user?.email ||
    userProfile?.email ||
    "";

  const [currentCopilotContext, setCurrentCopilotContext] = useState(() =>
    compactObject(copilotContext || {})
  );
  const routeContextFingerprintRef = useRef(getRouteContextFingerprint());

  const buildCopilotContext = useCallback(
    (extra = {}) => {
      const routeMode = getCurrentHashMode();
      const routeParams = getCurrentRouteParams();

      return compactObject({
        product: "OASYS",
        assistant: "OASYS Copilot",
        mode: routeMode,
        page: routeMode,
        surface: routeMode,
        intelligence_mode: "page",
        route_params: routeParams,
        tenant_id: resolvedTenantId,
        user_id: resolvedUserId,
        user_email: user?.email || "",
        plan: userProfile?.plan || "",
        ...copilotContext,
        ...currentCopilotContext,
        ...extra,
      });
    },
    [
      resolvedTenantId,
      resolvedUserId,
      user?.email,
      userProfile?.plan,
      copilotContext,
      currentCopilotContext,
    ]
  );

  // Chat state + streaming.
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const readerRef = useRef(null);
  const abortRef = useRef(null);
  const conversationIdRef = useRef(createCopilotCorrelationId("conversation"));
  const focusEpochIdRef = useRef(createCopilotCorrelationId("focus"));

  const copilotFocus = useMemo(
    () => getCopilotFocus(buildCopilotContext()),
    [buildCopilotContext]
  );
  const previousCopilotFocusRef = useRef(null);

  // For chip dedupe.
  const lastChipRef = useRef({ text: "", ts: 0 });

  // Workflow action registry. Active pages/surfaces can register safe,
  // user-triggered handlers while they are mounted. Copilot can then render
  // the action, but the owning page remains responsible for executing it.
  const workflowActionsRef = useRef(new Map());
  const [workflowActionsVersion, setWorkflowActionsVersion] = useState(0);

  const pushAssistantMessage = useCallback((content, extra = {}) => {
    const finalContent = String(content || "").trim();
    if (!finalContent) return;

    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content: finalContent,
        streaming: false,
        ...extra,
      },
    ]);
  }, []);

  const appendAssistantDelta = useCallback((delta, extra = {}) => {
    if (!delta && delta !== "") return;

    const safeExtra = extra && typeof extra === "object" ? extra : {};

    setMessages((prev) => {
      if (
        prev.length &&
        prev[prev.length - 1].role === "assistant" &&
        prev[prev.length - 1].streaming
      ) {
        const next = [...prev];

        next[next.length - 1] = {
          ...next[next.length - 1],
          ...safeExtra,
          content:
            (next[next.length - 1].content || "") + (delta || ""),
        };

        return next;
      }

      return [
        ...prev,
        {
          role: "assistant",
          content: delta || "",
          streaming: true,
          ...safeExtra,
        },
      ];
    });
  }, []);



  const replaceStreamingAssistant = useCallback((content, actions = [], extra = {}) => {
    const finalContent = String(content || "");
    const finalActions = Array.isArray(actions) ? actions : [];
    const safeExtra = extra && typeof extra === "object" ? extra : {};

    setMessages((prev) => {
      if (
        prev.length &&
        prev[prev.length - 1].role === "assistant" &&
        prev[prev.length - 1].streaming
      ) {
        const next = [...prev];

        next[next.length - 1] = {
          ...next[next.length - 1],
          ...safeExtra,
          content: finalContent,
          streaming: false,
          actions: finalActions,
        };

        return next;
      }

      return [
        ...prev,
        {
          role: "assistant",
          content: finalContent,
          streaming: false,
          actions: finalActions,
          ...safeExtra,
        },
      ];
    });
  }, []);


  const finalizeAssistant = useCallback(() => {
    setMessages((prev) => {
      if (!prev.length) return prev;

      const last = prev[prev.length - 1];
      if (last.role !== "assistant") return prev;

      const next = [...prev];
      next[next.length - 1] = {
        ...last,
        streaming: false,
      };

      return next;
    });
  }, []);

  const stopStreaming = useCallback(() => {
    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch {}
      abortRef.current = null;
    }

    readerRef.current = null;
    setIsStreaming(false);
  }, []);

  useEffect(() => {
    const nextFocus = copilotFocus;
    const previousFocus = previousCopilotFocusRef.current;

    if (!nextFocus) {
      if (previousFocus) {
        focusEpochIdRef.current = createCopilotCorrelationId("focus");
      }
      previousCopilotFocusRef.current = null;
      return;
    }

    const changed = Boolean(
      previousFocus && !sameCopilotFocus(previousFocus, nextFocus)
    );

    if (changed) {
      focusEpochIdRef.current = createCopilotCorrelationId("focus");

      if (isStreaming) {
        stopStreaming();
        finalizeAssistant();
      }

      const transitionContext = buildCopilotContext();
      const suggestions = getContextSuggestions(
        transitionContext,
        getCurrentHashMode()
      ).slice(0, 3);

      setMessages((prev) => {
        if (!prev.length) return prev;

        const last = prev[prev.length - 1];
        if (
          last?.role === "context" &&
          last?.type === "focus_transition" &&
          sameCopilotFocus(last?.focusSnapshot, nextFocus)
        ) {
          return prev;
        }

        return [
          ...prev,
          {
            role: "context",
            type: "focus_transition",
            content: "Focus changed",
            focusSnapshot: nextFocus,
            previousFocusSnapshot: previousFocus,
            focusEpochId: focusEpochIdRef.current,
            conversationBinding: getCopilotConversationBinding(
              sanitizeCopilotRequestContext(transitionContext),
              resolveCopilotRequestScope(transitionContext, getCurrentHashMode())
            ),
            suggestions,
            createdAt: new Date().toISOString(),
          },
        ];
      });
    }

    previousCopilotFocusRef.current = nextFocus;
  }, [
    buildCopilotContext,
    copilotFocus,
    finalizeAssistant,
    isStreaming,
    stopStreaming,
  ]);

  // Stable no-op kept for backwards compatibility with older UI references.
  const rotate = useCallback(() => {}, []);

  useEffect(() => {
    const compacted = compactObject(copilotContext || {});
    if (!Object.keys(compacted).length) return;

    setCurrentCopilotContext((prev) => compactObject({ ...prev, ...compacted }));
  }, [copilotContext]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncRouteContext = () => {
      const nextFingerprint = getRouteContextFingerprint();
      const previousFingerprint = routeContextFingerprintRef.current;

      if (
        previousFingerprint &&
        previousFingerprint !== nextFingerprint
      ) {
        focusEpochIdRef.current = createCopilotCorrelationId("focus");

        // Fail closed on page navigation: stale selection/state from the previous
        // surface must not silently become context for the next surface. The new
        // page can immediately register its own exact selection through
        // setCopilotContext().
        setCurrentCopilotContext(compactObject(copilotContext || {}));
      }

      routeContextFingerprintRef.current = nextFingerprint;
    };

    window.addEventListener("hashchange", syncRouteContext);
    return () => window.removeEventListener("hashchange", syncRouteContext);
  }, [copilotContext]);

  const setCopilotContext = useCallback((next = {}) => {
    const incoming = compactObject(next || {});

    setCurrentCopilotContext((prev) => {
      const base = hasCopilotSelectionUpdate(incoming)
        ? preserveCopilotUiContext(prev)
        : prev;

      return compactObject({
        ...base,
        ...incoming,
        context_updated_at: new Date().toISOString(),
      });
    });
  }, []);

  const clearCopilotContext = useCallback(() => {
    setCurrentCopilotContext({});
  }, []);

  const registerCopilotWorkflowAction = useCallback((actionId, handler, meta = {}) => {
    const id = String(actionId || "").trim();
    if (!id || typeof handler !== "function") {
      return () => {};
    }

    workflowActionsRef.current.set(id, {
      handler,
      meta: { ...(meta || {}), id },
      registered_at: new Date().toISOString(),
    });

    setWorkflowActionsVersion((version) => version + 1);

    return () => {
      const existing = workflowActionsRef.current.get(id);

      // Only unregister the same handler that registered this action. This
      // prevents a stale cleanup from removing a newer handler for the same id.
      if (!existing || existing.handler !== handler) return;

      workflowActionsRef.current.delete(id);
      setWorkflowActionsVersion((version) => version + 1);
    };
  }, []);

  const unregisterCopilotWorkflowAction = useCallback((actionId) => {
    const id = String(actionId || "").trim();
    if (!id) return;

    if (workflowActionsRef.current.delete(id)) {
      setWorkflowActionsVersion((version) => version + 1);
    }
  }, []);

  const runCopilotWorkflowAction = useCallback(async (actionId, payload = {}) => {
    const id = String(actionId || "").trim();
    if (!id) {
      throw new Error("Missing Copilot workflow action id.");
    }

    const action = workflowActionsRef.current.get(id);
    if (!action || typeof action.handler !== "function") {
      throw new Error(`Copilot workflow action is not registered: ${id}`);
    }

    return await action.handler(payload, {
      actionId: id,
      meta: action.meta || {},
      copilotContext: buildCopilotContext({
        workflow_action_id: id,
        workflow_action_source: "copilot",
      }),
    });
  }, [buildCopilotContext]);

  const availableCopilotWorkflowActions = useMemo(() => {
    // workflowActionsVersion intentionally forces recalculation when the ref map changes.
    void workflowActionsVersion;

    return Array.from(workflowActionsRef.current.entries()).map(([id, action]) => ({
      id,
      ...(action?.meta || {}),
      enabled: action?.meta?.enabled !== false,
    }));
  }, [workflowActionsVersion]);

  // Unified token fetcher.
  const fetchToken = useCallback(async () => {
    try {
      if (typeof getAccessToken === "function") {
        return await getAccessToken();
      }

      if (isAuthenticated && typeof getAccessTokenSilently === "function") {
        const audience = process.env.REACT_APP_AUTH0_AUDIENCE;

        return await getAccessTokenSilently(
          audience ? { authorizationParams: { audience } } : undefined
        );
      }
    } catch (e) {
      console.warn("[OASYS Copilot] Token fetch failed:", e);
    }

    return null;
  }, [getAccessToken, getAccessTokenSilently, isAuthenticated]);


  const runGovernedCopilotAction = useCallback(
    async (action = {}) => {
      const actionId = String(action?.id || "").trim();
      const actionType = String(action?.action_type || "").trim().toLowerCase();
      const actionEnvelopeType = String(action?.type || "").trim().toLowerCase();
      const authority = String(action?.authority || "").trim().toLowerCase();
      const method = String(action?.method || "").trim().toUpperCase();
      const taskId = String(action?.task_id || "").trim();
      const gateId = String(action?.gate_id || "").trim().toLowerCase();
      const endpoint = String(action?.endpoint || "").trim();

      if (
        !actionId ||
        actionEnvelopeType !== "governed_confirmation" ||
        actionType !== "confirm_prerequisites" ||
        authority !== "deterministic_oasys" ||
        method !== "POST" ||
        !taskId ||
        gateId !== "prerequisites" ||
        action?.confirmation_required !== true
      ) {
        throw new Error("This Copilot governed action is not valid for execution.");
      }

      const expectedEndpoint = `/v1/remediation/tasks/${taskId}/confirmations`;
      if (endpoint.replace(/\/+$/, "") !== expectedEndpoint) {
        throw new Error("This Copilot governed action endpoint does not match its task identity.");
      }

      if (!resolvedTenantId || !resolvedUserId) {
        throw new Error("Authenticated tenant and operator identity are required for this action.");
      }

      const token = await fetchToken();
      if (!token) {
        throw new Error("Authentication is required to execute this governed action.");
      }

      const preparedMetadata =
        action?.payload?.metadata && typeof action.payload.metadata === "object"
          ? action.payload.metadata
          : {};

      const body = {
        gate_id: "prerequisites",
        note: "Reviewed and confirmed the remediation prerequisites for the selected task.",
        metadata: compactObject({
          prepared_action_id: actionId,
          prepared_action_runtime: String(
            preparedMetadata.prepared_action_runtime || "oasys.copilot.governed_actions.v1"
          ).trim(),
          prepared_action_type: "confirm_prerequisites",
          request_surface: "copilot_governed_action",
        }),
      };

      const response = await fetch(joinGovernedActionUrl(effectiveApiBase, endpoint), {
        method: "POST",
        credentials: "include",
        headers: compactObject({
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": resolvedTenantId,
          "X-User-Id": resolvedUserId,
          "X-User-Name": resolvedUserName,
        }),
        body: JSON.stringify(body),
      });

      let payload = {};
      try {
        payload = await response.json();
      } catch {
        payload = {};
      }

      if (!response.ok) {
        const detail = payload?.detail;
        const errorCode =
          detail && typeof detail === "object"
            ? String(detail.code || "").trim()
            : "";
        const message =
          detail && typeof detail === "object"
            ? String(detail.message || "").trim()
            : typeof detail === "string"
            ? detail.trim()
            : "";

        if (errorCode === "remediation_confirmation_not_current") {
          setMessages((prev) =>
            prev.map((messageItem) => ({
              ...messageItem,
              actions: Array.isArray(messageItem?.actions)
                ? messageItem.actions.filter(
                    (messageAction) => String(messageAction?.id || "") !== actionId
                  )
                : messageItem?.actions,
            }))
          );
        }

        const error = new Error(
          message || `Governed Copilot action failed (${response.status}).`
        );
        error.code = errorCode;
        error.status = response.status;
        throw error;
      }

      // The exact prepared action is single-use in the visible transcript. The
      // backend remains authoritative and separately rejects stale/replayed state.
      setMessages((prev) =>
        prev.map((messageItem) => ({
          ...messageItem,
          actions: Array.isArray(messageItem?.actions)
            ? messageItem.actions.filter(
                (messageAction) => String(messageAction?.id || "") !== actionId
              )
            : messageItem?.actions,
        }))
      );

      return payload;
    },
    [
      effectiveApiBase,
      fetchToken,
      resolvedTenantId,
      resolvedUserId,
      resolvedUserName,
    ]
  );

  // Helpers.
  const isoHoursAgo = (h) => new Date(Date.now() - h * 3600 * 1000).toISOString();

  const linkTo = useCallback((params = {}) => {
    const qp = new URLSearchParams();

    const hasHashRouter =
      typeof window !== "undefined" &&
      (window.location.hash.startsWith("#/") ||
        window.location.hash.startsWith("#"));

    const mapStatus = (s) => (s === "acknowledged" ? "ack" : s);

    const normalizeDays = (p) => {
      const DEFAULT = HASH_WINDOW_DEFAULT_DAYS;

      if (p == null) return DEFAULT;
      if (typeof p === "number") return p;

      const m = String(p).match(/^(\d+)\s*d/i);
      return m ? Math.max(1, Number(m[1])) : DEFAULT;
    };

    const days =
      params.window_days != null
        ? Number(params.window_days)
        : normalizeDays(params.period);

    if (params.status) qp.set("status", mapStatus(String(params.status)));
    if (params.event) qp.set("event", String(params.event));

    qp.set("window_days", String(days));
    qp.set("window", String(days));

    if (hasHashRouter) {
      const root = HASH_COMPLIANCE_BASE || "compliance";
      return `#/${root}?${qp.toString()}`;
    }

    if (!qp.has("panel")) qp.set("panel", "compliance");
    return `${DASHBOARD_BASE}?${qp.toString()}`;
  }, []);

  const mdLink = (label, href) => `[${label}](${href})`;

  const formatRecentChanges = useCallback(
    (items) => {
      if (!items?.length) {
        return "No regulation changes detected in the last 24 hours.";
      }

      const rows = items.slice(0, 12).map((r) => {
        const tag = [r.framework, r.clause_id, r.change_type]
          .filter(Boolean)
          .join(" · ");

        const href = linkTo({
          status: "new",
          event: r.event_id,
          period: "90d",
        });

        const summary = r.change_summary || "—";
        return `${mdLink(tag, href)}: ${summary}`;
      });

      const more = items.length > 12 ? `\n…and ${items.length - 12} more.` : "";

      return `Here’s what changed in the last 24 hours:\n\n${rows.join("\n")}${more}`;
    },
    [linkTo]
  );

  const formatSummaryBuckets = useCallback(
    (summary) => {
      const s = summary || {};

      const parts = [
        { label: "Needs review", key: "new" },
        { label: "Acknowledged / in progress", key: "acknowledged" },
        { label: "Auto-resolved / processed", key: "processed" },
        { label: "Ignored / N/A", key: "ignored" },
      ];

      const mapStatusForLink = (k) => (k === "acknowledged" ? "ack" : k);

      const lines = parts.map(({ label, key }) => {
        const href = linkTo({
          status: mapStatusForLink(key),
          period: "90d",
        });

        const count = s[key] ?? 0;

        return `${mdLink(label, href)}: ${count}`;
      });

      return `Here’s the current workload split:\n\n${lines.join("\n")}`;
    },
    [linkTo]
  );

  const formatFocus = useCallback(
    (summary) => {
      const s = summary || {};
      const ordered = Object.entries(s).sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0));

      if (!ordered.length) {
        return "No recent activity yet today.";
      }

      const mapStatusForLink = (k) => (k === "acknowledged" ? "ack" : k);

      const top3 = ordered.slice(0, 3).map(([status, v]) => {
        const href = linkTo({
          status: mapStatusForLink(status),
          period: "1d",
        });

        return `${mdLink(status, href)}: ${v}`;
      });

      return `Automation focus today:\n\n${top3.join("\n")}`;
    },
    [linkTo]
  );

  const defaultNavigate = useCallback((href) => {
    const h = href.startsWith("#") ? href.slice(1) : href;

    if (typeof window !== "undefined") {
      window.location.hash = h.replace(/^#/, "");
    }
  }, []);

  // Derive Copilot mode from the canonical current route.
  const advisorKey = getCurrentHashMode();

  // Chip action resolvers.
  const runChipAction = useCallback(
    async (text) => {
      const promptContext = buildCopilotContext({
        prompt_source: "copilot_context_chip",
      });
      void promptContext;

      const token = await fetchToken();

      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      if (resolvedTenantId) headers["X-Tenant-Id"] = resolvedTenantId;
      if (resolvedUserId) headers["X-User-Id"] = resolvedUserId;

      if (/changed.*last\s*24\s*hours/i.test(text)) {
        const since = isoHoursAgo(24);

        const url = `${effectiveApiBase}/regulations/events/recent?since=${encodeURIComponent(
          since
        )}&limit=50`;

        const resp = await fetch(url, { headers });

        if (!resp.ok) {
          throw new Error(`recent changes ${resp.status}`);
        }

        const data = await resp.json();
        return formatRecentChanges(data);
      }

      if (/automation.*focusing|focus.*today|summarize workload/i.test(text)) {
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);

        const url = `${effectiveApiBase}/regulations/events/summary?since=${encodeURIComponent(
          startOfDay.toISOString()
        )}`;

        const resp = await fetch(url, { headers });

        if (!resp.ok) {
          throw new Error(`summary ${resp.status}`);
        }

        const summary = await resp.json();
        return formatFocus(summary);
      }

      if (/auto[-\s]?resolved|needs\s*review|workload/i.test(text)) {
        const since = isoHoursAgo(48);

        const url = `${effectiveApiBase}/regulations/events/summary?since=${encodeURIComponent(
          since
        )}`;

        const resp = await fetch(url, { headers });

        if (!resp.ok) {
          throw new Error(`summary ${resp.status}`);
        }

        const summary = await resp.json();
        return formatSummaryBuckets(summary);
      }

      if (/acknowledg(e|ement)s.*(7|seven)\s*days/i.test(text)) {
        const since = isoHoursAgo(24 * 7);

        const url = `${effectiveApiBase}/regulations/events/summary?since=${encodeURIComponent(
          since
        )}`;

        const resp = await fetch(url, { headers });
        const s = resp.ok ? await resp.json() : {};
        const count = s?.acknowledged ?? 0;
        const href = linkTo({ status: "ack", period: "7d" });

        return `Acknowledgements in the last 7 days: **${count}**\n\n${mdLink(
          "View acknowledged",
          href
        )}`;
      }

      if (/ignored.*(week|7\s*days)/i.test(text)) {
        const since = isoHoursAgo(24 * 7);

        const url = `${effectiveApiBase}/regulations/events/summary?since=${encodeURIComponent(
          since
        )}`;

        const resp = await fetch(url, { headers });
        const s = resp.ok ? await resp.json() : {};
        const count = s?.ignored ?? 0;
        const href = linkTo({ status: "ignored", period: "7d" });

        return `Ignored items this week: **${count}**\n\n${mdLink(
          "View ignored",
          href
        )}`;
      }

      if (/processed|auto[-\s]?resolved.*(week|7\s*days)/i.test(text)) {
        const since = isoHoursAgo(24 * 7);

        const url = `${effectiveApiBase}/regulations/events/summary?since=${encodeURIComponent(
          since
        )}`;

        const resp = await fetch(url, { headers });
        const s = resp.ok ? await resp.json() : {};
        const count = s?.processed ?? 0;
        const href = linkTo({ status: "processed", period: "7d" });

        return `Processed items this week: **${count}**\n\n${mdLink(
          "View processed",
          href
        )}`;
      }

      return null;
    },
    [
      buildCopilotContext,
      effectiveApiBase,
      fetchToken,
      resolvedTenantId,
      resolvedUserId,
      formatFocus,
      formatRecentChanges,
      formatSummaryBuckets,
      linkTo,
    ]
  );

  /**
   * Message sender.
   */
  const sendMessage = useCallback(
    async ({ text, echoUser = true, context = {} }) => {
      setError(null);

      const content = (text || "").trim();
      if (!content) return;

      stopStreaming();

      const localContext = buildCopilotContext(context);
      const requestFocus = getCopilotFocus(localContext);
      const requestContext = sanitizeCopilotRequestContext(localContext);
      const requestScope = resolveCopilotRequestScope(
        requestContext,
        advisorKey || "mission_control"
      );
      const requestBinding = getCopilotConversationBinding(
        requestContext,
        requestScope
      );
      const requestFocusEpochId = focusEpochIdRef.current;
      const currentMessageId = createCopilotCorrelationId("message");
      const assistantMessageId = createCopilotCorrelationId("message");

      if (echoUser) {
        setMessages((prev) => [
          ...prev,
          {
            role: "user",
            content,
            focusSnapshot: requestFocus,
            focusEpochId: requestFocusEpochId,
            conversationBinding: requestBinding,
            messageId: currentMessageId,
          },
        ]);
      }

      setIsStreaming(true);

      // Immediately create an empty assistant to show typing indicator and bind
      // the response to the focus that was active when this request began.
      appendAssistantDelta("", {
        focusSnapshot: requestFocus,
        focusEpochId: requestFocusEpochId,
        conversationBinding: requestBinding,
        messageId: assistantMessageId,
      });

      const conversation = buildCopilotConversationEnvelope({
        messages,
        requestContext,
        requestScope,
        conversationId: conversationIdRef.current,
        focusEpochId: requestFocusEpochId,
        currentMessageId,
      });

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const token = await fetchToken();

        const headers = {
          "Content-Type": "application/json",
          Accept: "application/json, text/event-stream",
        };

        if (token) headers.Authorization = `Bearer ${token}`;
        if (resolvedTenantId) headers["X-Tenant-Id"] = resolvedTenantId;
        if (resolvedUserId) headers["X-User-Id"] = resolvedUserId;

        const res = await fetch(askUrl, {
          method: "POST",
          headers,
          body: JSON.stringify({
            message: content,
            question: content,
            scope: requestScope,
            context: requestContext,
            copilot_context: requestContext,
            conversation,
          }),
          signal: controller.signal,
          credentials: "include",
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => "");

          throw new Error(
            `Copilot request failed: ${res.status} ${res.statusText}${
              txt ? ` — ${txt}` : ""
            }`
          );
        }

        const contentType = (res.headers.get("content-type") || "").toLowerCase();

        if (
          !res.body ||
          (!contentType.includes("event-stream") &&
            !contentType.includes("stream"))
        ) {
          try {
            const json = await res.json();

            const finalText =
              json?.content ||
              json?.message ||
              json?.answer ||
              json?.response ||
              JSON.stringify(json);

            const responseCard =
              json?.card && typeof json.card === "object" ? json.card : null;

            const responseActions = Array.isArray(json?.actions) ? json.actions : [];

            replaceStreamingAssistant(finalText, responseActions, {
              card: responseCard,
              focusSnapshot: requestFocus,
              interactionId: json?.interaction_id || responseCard?.metadata?.interaction_id || null,
              promptVersion: json?.prompt_version || responseCard?.metadata?.prompt_version || null,
              promptVersionId: json?.prompt_version_id || responseCard?.metadata?.prompt_version_id || null,
            });
          } catch {
            const txt = await res.text().catch(() => "");
            replaceStreamingAssistant(txt || "(empty response)");
          }

          setIsStreaming(false);

          if (abortRef.current === controller) {
            abortRef.current = null;
          }

          readerRef.current = null;
          return;
        }

        const reader = res.body.getReader();
        readerRef.current = reader;

        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();

          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const chunks = buffer.split(/\r?\n\r?\n/);
          buffer = chunks.pop() || "";

          for (const chunk of chunks) {
            const lines = chunk
              .split(/\r?\n/)
              .map((l) => (l.startsWith("data:") ? l.slice(5).trim() : l.trim()))
              .filter(Boolean);

            if (!lines.length) continue;

            const piece = lines.join("\n");

            try {
              const evt = JSON.parse(piece);

              if (typeof evt.delta === "string") {
                appendAssistantDelta(evt.delta);
              }

              if (evt.type === "message" && typeof evt.content === "string") {
                appendAssistantDelta(evt.content);
              }

              if (evt.done || evt.type === "done") {
                break;
              }

              if (evt.error) {
                setError(evt.error);
                break;
              }
            } catch {
              appendAssistantDelta(piece);
            }
          }
        }

        const tail = buffer.trim();

        if (tail) {
          try {
            const evt = JSON.parse(tail);

            if (typeof evt.delta === "string") {
              appendAssistantDelta(evt.delta);
            }

            if (evt.type === "message" && typeof evt.content === "string") {
              appendAssistantDelta(evt.content);
            }
          } catch {
            appendAssistantDelta(tail);
          }
        }
      } catch (e) {
        if (e?.name !== "AbortError") {
          console.error("OASYS Copilot sendMessage error:", e);
          setError(String(e?.message || e));
          appendAssistantDelta("\n\n_(Sorry—something went wrong.)_");
        }
      } finally {
        finalizeAssistant();
        setIsStreaming(false);

        if (abortRef.current === controller) {
          abortRef.current = null;
        }

        readerRef.current = null;
      }
    },
    [
      fetchToken,
      askUrl,
      appendAssistantDelta,
      finalizeAssistant,
      stopStreaming,
      resolvedTenantId,
      resolvedUserId,
      buildCopilotContext,
      replaceStreamingAssistant,
      advisorKey,
      messages,
    ]
  );

  // Chip sender.
  const sendChip = useCallback(
    async (text) => {
      const content = (text || "").trim();

      if (!content || isStreaming) return;

      // Simple dedupe for rapid double taps.
      const now = Date.now();

      if (
        lastChipRef.current.text === content &&
        now - lastChipRef.current.ts < 800
      ) {
        return;
      }

      lastChipRef.current = {
        text: content,
        ts: now,
      };

      setError(null);

      const chipContext = buildCopilotContext();
      const chipFocus = getCopilotFocus(chipContext);
      const chipRequestContext = sanitizeCopilotRequestContext(chipContext);
      const chipScope = resolveCopilotRequestScope(
        chipRequestContext,
        advisorKey || "mission_control"
      );
      const chipBinding = getCopilotConversationBinding(
        chipRequestContext,
        chipScope
      );
      const chipFocusEpochId = focusEpochIdRef.current;

      // Echo the chip as the user's message once and preserve the focus under
      // which the operator initiated it.
      setMessages((prev) => [
        ...prev,
        {
          role: "user",
          content,
          focusSnapshot: chipFocus,
          focusEpochId: chipFocusEpochId,
          conversationBinding: chipBinding,
          messageId: createCopilotCorrelationId("message"),
        },
      ]);

      setIsStreaming(true);
      appendAssistantDelta("", {
        focusSnapshot: chipFocus,
        focusEpochId: chipFocusEpochId,
        conversationBinding: chipBinding,
        messageId: createCopilotCorrelationId("message"),
      });

      try {
        const answer = COPILOT_GOVERNED_ASK_ONLY
          ? null
          : await runChipAction(content);

        if (answer) {
          const answerContent =
            typeof answer === "string" ? answer : answer?.content || answer?.message || "";
          const answerActions =
            typeof answer === "object" && Array.isArray(answer?.actions) ? answer.actions : [];

          await sleep(getCopilotThinkDelay());
          replaceStreamingAssistant(answerContent, answerActions);
          setIsStreaming(false);
          return;
        }
      } catch (e) {
        console.error("Copilot chip action failed:", e);

        replaceStreamingAssistant("I couldn't retrieve that data just now. Please try again in a moment.");
        setIsStreaming(false);
        return;
      }

      // Fall back to LLM but DO NOT echo user again.
      await sendMessage({
        text: content,
        echoUser: false,
      });
    },
    [
      isStreaming,
      runChipAction,
      sendMessage,
      appendAssistantDelta,
      replaceStreamingAssistant,
      buildCopilotContext,
      advisorKey,
    ]
  );

  const contextSuggestions = useMemo(
    () => getContextSuggestions(buildCopilotContext(), advisorKey),
    [advisorKey, buildCopilotContext]
  );

  const value = useMemo(
    () => ({
      advisor: advisorPersona,
      rotate,
      identity,
      advisorKey,
      messages,
      isStreaming,
      error,
      sendMessage,
      sendChip,
      pushAssistantMessage,
      stopStreaming,
      onNavigate: onNavigate || defaultNavigate,
      copilotContext: buildCopilotContext(),
      governedCopilotContext: sanitizeCopilotRequestContext(buildCopilotContext()),
      copilotFocus,
      contextSuggestions,
      setCopilotContext,
      clearCopilotContext,
      availableCopilotWorkflowActions,
      registerCopilotWorkflowAction,
      unregisterCopilotWorkflowAction,
      runCopilotWorkflowAction,
      runGovernedCopilotAction,
    }),
    [
      advisorPersona,
      rotate,
      identity,
      advisorKey,
      messages,
      isStreaming,
      error,
      sendMessage,
      sendChip,
      pushAssistantMessage,
      stopStreaming,
      onNavigate,
      defaultNavigate,
      buildCopilotContext,
      copilotFocus,
      contextSuggestions,
      setCopilotContext,
      clearCopilotContext,
      availableCopilotWorkflowActions,
      registerCopilotWorkflowAction,
      unregisterCopilotWorkflowAction,
      runCopilotWorkflowAction,
      runGovernedCopilotAction,
    ]
  );

  return <AdvisorContext.Provider value={value}>{children}</AdvisorContext.Provider>;
}

export const useAdvisor = () => useContext(AdvisorContext);